import {
  lazy, Suspense, useCallback, useEffect, useRef, useState,
} from 'react';
import { useTheme, useTokens } from '../theme';
import { getInstitutionAnalytics } from '../services/institutions';
import { VIEW_TO_PATH } from '../../portal/portalUrl.js';
import { Switch } from '../Switch.jsx';
import {
  Card, SectionTitle, Stat, StatRow, Pill, StatusBadge, Note, ErrorState, pct, num,
} from '../uiKit';
import {
  ChartSkeleton, MetricsSkeleton, SkeletonBlock, TableSkeleton,
} from '../Skeleton.jsx';
import TimeSavedSection from './TimeSavedSection.jsx';
import { readTimeSavedHint, writeTimeSavedHint } from '../timeSavedHint.js';

const TrendChart = lazy(() => { return import('../TrendChart.jsx'); });

function ChartSkeletonItems({ count = 4 }) {
  return Array.from({ length: count }, (_, index) => {
    return <ChartSkeleton key={index} />;
  });
}

function ChartGridSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <ChartSkeletonItems count={count} />
    </div>
  );
}

function AnalyticsLoadingSkeleton({ everyone, timeSaved }) {
  const t = useTokens();
  const sectionTitle = {
    color: t.heading,
    fontFamily: t.fonts.display,
  };
  const section = {
    borderColor: t.hairline,
  };
  return (
    <div role="status" aria-label="Loading analytics" aria-busy="true">
      <span className="sr-only">Loading analytics</span>
      <MetricsSkeleton count={6} className="xl:grid-cols-6" />
      <SkeletonBlock className="mt-3 h-3 w-3/5 max-w-xl" />

      {/* TimeSavedSection: 4 stats, then 4 action rows (a table from 640px,
          one card each below it), a note and the "How we estimate" link.
          Drawn only when this school's last read carried the block. */}
      {timeSaved ? (
        <div className="mt-6 border-t pt-5" style={section}>
          <h4 className="text-base font-semibold" style={sectionTitle}>Time saved</h4>
          <SkeletonBlock className="mt-2 h-4 w-3/4 max-w-2xl" />
          <MetricsSkeleton count={4} className="mt-4" />
          <TableSkeleton rows={4} columns={5} className="mt-4 hidden sm:block" />
          <div className="mt-4 grid gap-2 sm:hidden">
            {[0, 1, 2, 3].map((item) => { return <SkeletonBlock key={item} className="h-36 w-full" />; })}
          </div>
          <SkeletonBlock className="mt-3 h-4 w-2/3 max-w-xl" />
          <div className="mt-3 flex min-h-[44px] items-center"><SkeletonBlock className="h-4 w-32" /></div>
        </div>
      ) : null}

      <div className="mt-6 border-t pt-5" style={section}>
        <h4 className="text-base font-semibold" style={sectionTitle}>{everyone ? 'School-wide trends' : 'Program trends'}</h4>
        <SkeletonBlock className="mb-4 mt-2 h-4 w-4/5 max-w-2xl" />
        <ChartGridSkeleton count={4} />
      </div>

      <div className="mt-6 border-t pt-5" style={section}>
        <h4 className="text-base font-semibold" style={sectionTitle}>Outreach audience</h4>
        <SkeletonBlock className="mt-2 h-4 w-3/4 max-w-2xl" />
        <MetricsSkeleton count={3} className="mt-4 xl:grid-cols-3" />
      </div>

      <div className="mt-6 border-t pt-5" style={section}>
        <h4 className="text-base font-semibold" style={sectionTitle}>Product usage</h4>
        <SkeletonBlock className="mt-2 h-4 w-3/4 max-w-2xl" />
        <div className="mt-4"><ChartGridSkeleton count={4} /></div>
        <MetricsSkeleton count={4} className="mt-4" />
        <MetricsSkeleton count={4} className="mt-3" />
        <MetricsSkeleton count={5} className="mt-3 xl:grid-cols-5" />
      </div>

      <div className="mt-6 border-t pt-5" style={section}>
        <h4 className="text-base font-semibold" style={sectionTitle}>Coffee-chat signals</h4>
        <SkeletonBlock className="mt-2 h-4 w-3/4 max-w-2xl" />
        <MetricsSkeleton count={8} className="mt-4 xl:grid-cols-8" />
        <MetricsSkeleton count={6} className="mt-3 xl:grid-cols-6" />
      </div>
    </div>
  );
}

const usageNumber = (value) => {
  return value === null || value === undefined ? 'Private' : Number(value).toLocaleString();
};

function AudienceBreakdown({ title, rows }) {
  const t = useTokens();
  const visible = (rows || [])
    .filter((row) => { return !row.suppressed && row.count !== null && row.count !== undefined; })
    .slice(0, 8);
  const max = Math.max(...visible.map((row) => { return Number(row.count) || 0; }), 1);
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
      <h5 className="text-sm font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>{title}</h5>
      {visible.length ? (
        <div className="mt-4 space-y-3">
          {visible.map((row) => {
            const width = `${Math.max(4, ((Number(row.count) || 0) / max) * 100)}%`;
            return (
              <div key={row.label}>
                <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
                  <span className="truncate" title={row.label} style={{ color: t.body, fontFamily: t.fonts.body }}>{row.label}</span>
                  <span style={{ color: t.muted, fontFamily: t.fonts.mono }}>{usageNumber(row.count)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: t.border }}>
                  <div className="h-full rounded-full" style={{ width, backgroundColor: t.primaryBg }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-3 text-sm" style={{ color: t.muted }}>Not enough data yet.</p>
      )}
      {(rows || []).some((row) => { return row.suppressed; }) ? (
        <Note className="mt-3">Smaller groups are hidden for privacy.</Note>
      ) : null}
    </div>
  );
}

function OutreachAudience({ audience }) {
  const t = useTokens();
  if (!audience) { return null; }
  return (
    <div className="mt-6 border-t pt-5" style={{ borderColor: t.hairline }}>
      <h4 className="text-base font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>Outreach audience</h4>
      <p className="mt-1 text-sm" style={{ color: t.muted }}>
        Distinct people students reached in the selected range, grouped by enriched recipient profile. Follow-ups are counted once.
      </p>
      {audience.suppressed ? (
        <p className="mt-4 text-sm" style={{ color: t.muted }}>
          Audience breakdowns will appear when enough distinct students contribute outreach data.
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm" style={{ color: t.muted, fontFamily: t.fonts.body }}>
            {usageNumber(audience.totalRecipients)} distinct recipients across {usageNumber(audience.contributingStudents)} students
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
            <AudienceBreakdown title="Industry" rows={audience.industries} />
            <AudienceBreakdown title="Sub-industry" rows={audience.subIndustries} />
            <AudienceBreakdown title="Seniority" rows={audience.seniority} />
          </div>
        </>
      )}
    </div>
  );
}

function UsageAnalytics({ usage }) {
  const t = useTokens();
  if (!usage || usage.suppressed || !usage.totals) { return null; }
  const totals = usage.totals;
  const periods = usage.periods || [];
  return (
    <div className="mt-6 border-t pt-5" style={{ borderColor: t.hairline }}>
      <div className="mb-3">
        <h4 className="text-base font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>Product usage</h4>
        <p className="mt-1 text-sm" style={{ color: t.muted }}>
          Aggregate billing-period trends and current usage. No student-level activity is included.
        </p>
      </div>

      {periods.length ? (
        <div className="mb-6 mt-5">
          <div className="mb-1 text-xs uppercase tracking-wide" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Usage over time</div>
          <p className="mb-4 text-sm" style={{ color: t.muted }}>
            Each bar is the institution&apos;s aggregate consumption during one billing period.
          </p>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Suspense fallback={<ChartSkeletonItems count={4} />}>
              <TrendChart
                title="Core usage"
                data={periods}
                xKey="periodStart"
                variant="bar"
                height={220}
                series={[
                  { key: 'aiCreditsUsed', label: 'Coffee credits', color: '#E95432' },
                  { key: 'contactsReached', label: 'Contacts', color: '#6F4E37' },
                  { key: 'projectsCreated', label: 'Projects', color: '#5A7A8C' },
                ]}
              />
              <TrendChart
                title="Practice and research"
                data={periods}
                xKey="periodStart"
                variant="bar"
                height={220}
                series={[
                  { key: 'aiAvatarMinutesUsed', label: 'Avatar min', color: '#9A745C' },
                  { key: 'aiVoiceAgentMinutesUsed', label: 'Voice min', color: '#5A7A8C' },
                  { key: 'standardResearchUsed', label: 'Standard research', color: '#6F4E37' },
                  { key: 'deepResearchUsed', label: 'Deep research', color: '#E95432' },
                ]}
              />
              <TrendChart
                title="AI allowance token volume"
                data={periods}
                xKey="periodStart"
                variant="bar"
                height={220}
                series={[
                  { key: 'aiChatTokensUsed', label: 'Included tokens', color: '#5A7A8C' },
                  { key: 'overageChatTokensUsed', label: 'Overage tokens', color: '#E95432' },
                ]}
              />
              <TrendChart
                title="Overage usage"
                data={periods}
                xKey="periodStart"
                variant="bar"
                height={220}
                series={[
                  { key: 'overageCreditsUsed', label: 'Credits', color: '#E95432' },
                  { key: 'overageAvatarMinutesUsed', label: 'Avatar min', color: '#9A745C' },
                  { key: 'overageVoiceMinutesUsed', label: 'Voice min', color: '#5A7A8C' },
                ]}
              />
            </Suspense>
          </div>
        </div>
      ) : (
        <p className="mb-5 mt-4 text-sm" style={{ color: t.muted }}>
          Usage trends will appear after the first billing period is recorded.
        </p>
      )}

      <div className="mb-2 text-xs uppercase tracking-wide" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Current billing period</div>
      <StatRow>
        <Stat label="Coffee credits" value={usageNumber(totals.aiCreditsUsed)} />
        <Stat label="Contacts" value={usageNumber(totals.contactsReached)} />
        <Stat label="Projects" value={usageNumber(totals.projectsCreated)} />
        <Stat label="AI allowance tokens" value={usageNumber(totals.aiChatTokensUsed)} />
      </StatRow>
      <div className="mt-3"><StatRow>
        <Stat label="Avatar minutes" value={usageNumber(totals.aiAvatarMinutesUsed)} />
        <Stat label="Voice minutes" value={usageNumber(totals.aiVoiceAgentMinutesUsed)} />
        <Stat label="Standard research" value={usageNumber(totals.standardResearchUsed)} />
        <Stat label="Deep research" value={usageNumber(totals.deepResearchUsed)} />
      </StatRow></div>
      <div className="mt-3"><StatRow>
        <Stat label="Overage credits" value={usageNumber(totals.overageCreditsUsed)} />
        <Stat label="Overage avatar min" value={usageNumber(totals.overageAvatarMinutesUsed)} />
        <Stat label="Overage voice min" value={usageNumber(totals.overageVoiceMinutesUsed)} />
        <Stat label="AI allowance overage tokens" value={usageNumber(totals.overageChatTokensUsed)} />
      </StatRow></div>
    </div>
  );
}

const percentValue = (value) => {
  return value === null || value === undefined ? 'Private' : `${Math.round(value * 100)}%`;
};
const secondsValue = (value) => {
  return value === null || value === undefined ? 'Private' : `${(value / 1000).toFixed(1)}s`;
};

function ProgramTimeSeries({ trends, signals, everyone }) {
  const t = useTokens();
  if (!trends) { return null; }
  if (trends.suppressed) {
    return <p className="mt-5 text-sm" style={{ color: t.muted }}>Weekly trends will appear when this cohort meets the privacy threshold.</p>;
  }
  const weeks = trends.weeks || [];
  const signalByWeek = new Map((signals?.weeks || []).map((row) => { return [row.week, row]; }));
  const signalWeeks = weeks.map((weekRow) => {
    const row = signalByWeek.get(weekRow.week) || { week: weekRow.week };
    return {
      ...row,
      hostTalkPercent: row.avgTalkRatioHost === null || row.avgTalkRatioHost === undefined
        ? null : Math.round(row.avgTalkRatioHost * 100),
      silencePercent: row.avgSilenceRatio === null || row.avgSilenceRatio === undefined
        ? null : Math.round(row.avgSilenceRatio * 100),
    };
  });
  return (
    <div className="mt-6 border-t pt-5" style={{ borderColor: t.hairline }}>
      <h4 className="text-base font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>
        {everyone ? 'School-wide trends' : 'Program trends'}
      </h4>
      <p className="mt-1 mb-4 text-sm" style={{ color: t.muted }}>
        Weekly aggregate participation, outreach, and coffee-chat signals for the selected range.
      </p>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Suspense fallback={<ChartSkeletonItems count={4} />}>
          <TrendChart
            title="Participation over time"
            data={weeks}
            xKey="week"
            height={220}
            series={[
              { key: 'studentsNetworking', label: 'Students networking', color: '#6F4E37' },
              { key: 'coffeeChats', label: 'Coffee chats', color: '#E95432' },
            ]}
          />
          <TrendChart
            title="Outreach over time"
            data={weeks}
            xKey="week"
            height={220}
            series={[
              { key: 'emailsSent', label: 'Emails', color: '#E95432' },
              { key: 'followUps', label: 'Follow-ups', color: '#6F4E37' },
              { key: 'emailOpens', label: 'Opens', color: '#5A7A8C' },
            ]}
          />
          <TrendChart
            title="Coffee-chat time over time"
            data={weeks}
            xKey="week"
            variant="bar"
            height={220}
            series={[{ key: 'coffeeChatMinutes', label: 'Meeting minutes', color: '#9A745C' }]}
          />
          <TrendChart
            title="Conversation balance over time"
            data={signalWeeks}
            xKey="week"
            height={220}
            series={[
              { key: 'hostTalkPercent', label: 'Student talk %', color: '#E95432' },
              { key: 'silencePercent', label: 'Silence %', color: '#5A7A8C' },
            ]}
          />
        </Suspense>
      </div>
    </div>
  );
}

function CoffeeChatSignals({ signals }) {
  const t = useTokens();
  if (!signals) { return null; }
  const quantitative = signals.quantitative;
  const qualitative = (signals.qualitative || []).filter((row) => {
    return !row.suppressed && row.averageScore !== null && row.averageScore !== undefined;
  });
  const hasQuantitative = quantitative && !quantitative.suppressed && Number(quantitative.meetings) > 0;
  if (!hasQuantitative && qualitative.length === 0) {
    return (
      <div className="mt-6 border-t pt-5" style={{ borderColor: t.hairline }}>
        <h4 className="text-base font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>Coffee-chat signals</h4>
        <p className="mt-1 text-sm" style={{ color: t.muted }}>
          {quantitative?.suppressed
            ? 'Conversation signals will appear when enough distinct students contribute data.'
            : 'Conversation signals will appear after students complete qualifying coached coffee chats.'}
        </p>
      </div>
    );
  }
  const categoryLabel = (value) => {
    // Stored as referralNavigation, judged against each call's goal and shown
    // as "Next step" on every surface (COF-604).
    if (value === 'referralNavigation') return 'Next step';
    // Sentence case, as in the student app ("Question depth").
    return String(value).replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, (letter) => { return letter.toUpperCase(); });
  };
  return (
    <div className="mt-6 border-t pt-5" style={{ borderColor: t.hairline }}>
      <h4 className="text-base font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>Coffee-chat signals</h4>
      <p className="mt-1 mb-4 text-sm" style={{ color: t.muted }}>
        Aggregate numeric patterns from completed conversations. Transcripts, evidence, and narrative feedback remain private.
      </p>
      {hasQuantitative ? (
        <>
          <div className="mb-2 text-xs uppercase tracking-wide" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Quantitative</div>
          <StatRow>
            <Stat label="Student talk" value={percentValue(quantitative.avgTalkRatioHost)} />
            <Stat label="Response time" value={secondsValue(quantitative.avgResponseLatencyMedianMs)} />
            <Stat label="Longest monologue" value={secondsValue(quantitative.avgLongestMonologueMs)} />
            <Stat label="Questions / chat" value={usageNumber(quantitative.avgQuestionsAsked)} />
            <Stat label="Questions / 10 min" value={usageNumber(quantitative.avgQuestionRatePer10Min)} />
            <Stat label="Silence" value={percentValue(quantitative.avgSilenceRatio)} />
            <Stat label="Speaking pace" value={quantitative.avgSpeakingPaceWpm === null || quantitative.avgSpeakingPaceWpm === undefined ? 'Private' : `${quantitative.avgSpeakingPaceWpm} wpm`} />
            <Stat label="Filler words" value={quantitative.avgFillerRatePerMinHost === null || quantitative.avgFillerRatePerMinHost === undefined ? 'Private' : `${quantitative.avgFillerRatePerMinHost.toFixed(1)} / min`} />
            <Stat label="Referral readiness" value={quantitative.avgReferralReadinessPeak === null || quantitative.avgReferralReadinessPeak === undefined ? 'Private' : `${quantitative.avgReferralReadinessPeak}/100`} />
          </StatRow>
          <p className="mt-2 text-sm" style={{ color: t.muted, fontFamily: t.fonts.body }}>
            {usageNumber(quantitative.meetings)} qualifying chats across {usageNumber(quantitative.contributingStudents)} students
          </p>
        </>
      ) : null}
      {qualitative.length ? (
        <div className="mt-5">
          <div className="mb-2 text-xs uppercase tracking-wide" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Qualitative signals</div>
          <StatRow>
            {qualitative.map((row) => {
              return <Stat key={row.category} label={categoryLabel(row.category)} value={`${row.averageScore.toFixed(1)} / 5`} />;
            })}
          </StatRow>
        </div>
      ) : null}
    </div>
  );
}

// Turn a labelKey ("major", "graduationYear") into a readable column header.
const humanizeKey = (key) => {
  const spaced = String(key || '').replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * States how much of the referral figures rest on student-recorded outcomes
 * versus estimates. Chats where neither exists count as "no referral", so the
 * rates read low; without this line that bias is invisible.
 */
function ReferralCoverageNote({ coverage }) {
  if (!coverage) {
    return null;
  }
  const covered = (coverage.tagged || 0) + (coverage.inferred || 0);
  const total = covered + (coverage.uncovered || 0);
  if (total === 0) {
    return null;
  }
  return (
    <Note className="mt-3">
      {`Referral figures cover ${covered} of ${total} held chats`}
      {coverage.inferred ? `, ${coverage.inferred} estimated from call signals` : ''}
      {'. Chats with no recorded outcome count as no referral.'}
    </Note>
  );
}

// `countLabel` names the denominator. Major and graduation year count students
// (one row each); industry counts chats, because industry belongs to the person
// met rather than to the student. Showing the difference in the header keeps the
// two rate kinds from reading as one.
function CohortTable({
  title, rows, labelKey, countLabel = 'Students', countKey = 'count',
}) {
  const t = useTokens();
  if (!rows || rows.length === 0) {
    return null;
  }
  const cell = { borderBottom: `1px solid ${t.hairline}` };
  const headCell = 'py-2 text-xs uppercase tracking-wide font-normal';
  const headStyle = { fontFamily: t.fonts.mono, color: t.muted, ...cell };
  return (
    <div className="mt-4">
      <div
        className="mb-1 text-sm font-semibold"
        style={{ fontFamily: t.fonts.body, color: t.heading }}
      >
        {title}
      </div>
      <div className="grid gap-2 md:hidden">
        {rows.map((row, idx) => {
          return (
            <article key={`${row[labelKey]}-mobile-${idx}`} className="rounded-lg border p-3" style={{ borderColor: t.border, backgroundColor: t.surface }}>
              <div className="text-sm font-semibold" style={{ color: t.heading, fontFamily: t.fonts.mono }}>
                {String(row[labelKey])}
              </div>
              {row.suppressed ? (
                <p className="mt-2 text-sm italic" style={{ color: t.muted }}>Not enough data</p>
              ) : (
                <dl className="mt-3 grid grid-cols-2 gap-3">
                  <div><dt className="text-[11px] uppercase tracking-wide" style={{ color: t.muted }}>{countLabel}</dt><dd className="mt-1 text-sm" style={{ color: t.body, fontFamily: t.fonts.mono }}>{row[countKey]}</dd></div>
                  <div><dt className="text-[11px] uppercase tracking-wide" style={{ color: t.muted }}>Referred</dt><dd className="mt-1 text-sm" style={{ color: t.body, fontFamily: t.fonts.mono }}>{pct(row.referralRate)}</dd></div>
                </dl>
              )}
            </article>
          );
        })}
      </div>
      <table className="hidden w-full border-collapse md:table">
        <thead>
          <tr>
            <th scope="col" className={`text-left ${headCell}`} style={headStyle}>{humanizeKey(labelKey)}</th>
            <th scope="col" className={`text-right ${headCell}`} style={headStyle}>{countLabel}</th>
            <th scope="col" className={`text-right ${headCell}`} style={headStyle}>Referred</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            return (
              <tr key={`${row[labelKey]}-${idx}`}>
                <th
                  scope="row"
                  className="text-left py-2 pr-3 text-sm font-normal"
                  style={{ fontFamily: t.fonts.mono, color: t.body, ...cell }}
                >
                  {String(row[labelKey])}
                </th>
                {row.suppressed ? (
                  <td
                    colSpan={2}
                    className="text-right py-2 text-sm italic"
                    style={{ fontFamily: t.fonts.body, color: t.muted, ...cell }}
                  >
                    Not enough data
                  </td>
                ) : (
                  <>
                    <td className="text-right py-2 text-sm" style={{ fontFamily: t.fonts.mono, color: t.body, ...cell }}>
                      {row[countKey]}
                    </td>
                    <td className="text-right py-2 text-sm" style={{ fontFamily: t.fonts.mono, color: t.body, ...cell }}>
                      {pct(row.referralRate)}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * k-anonymized analytics for an institution. `institutionId` scopes it for a
 * super-admin; omit it and an institution admin sees their own school.
 *
 * Only the super-admin view offers non-program users (everyone on the school's
 * verified domains). Schools are promised analytics over consenting students
 * alone, and the API ignores the flag for them anyway.
 *
 * Below the cohort floor a school sees one block instead of filters that change
 * nothing: suppression follows the consented population, not the range. The
 * consented count itself stays hidden there (the API nulls it). Pass
 * `canManageSeats` to add a link to Seats & funding in that block. A segment
 * view hidden only because another segment is small says that instead.
 *
 * `slug` (the portal) or `institutionId` (the console) keys the time-saved
 * skeleton hint (timeSavedHint.js).
 */
export default function AnalyticsPanel({ institutionId, slug, canManageSeats = false }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  const [data, setData] = useState(null);
  const [segment, setSegment] = useState('all');
  const [weeks, setWeeks] = useState(12);
  const [includeNonProgram, setIncludeNonProgram] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // The school-wide fetch is slower, so a quick on-then-off could otherwise let
  // the stale wide response land last. Only the latest request may write.
  const latestRequest = useRef(0);
  const hintKey = institutionId || slug;

  const load = useCallback(async (seg) => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    setLoading(true);
    let next = null;
    let loadFailed = false;
    try {
      next = await getInstitutionAnalytics(seg === 'all' ? undefined : seg, institutionId, weeks, includeNonProgram);
    } catch (err) {
      next = null;
      loadFailed = true;
    }
    if (request === latestRequest.current) {
      if (!loadFailed) { writeTimeSavedHint(hintKey, Boolean(next?.timeSaved)); }
      setData(next);
      setFailed(loadFailed);
      setLoading(false);
    }
  }, [institutionId, weeks, includeNonProgram, hintKey]);

  useEffect(() => { load(segment); }, [load, segment]);

  const report = data?.report;
  const platformOverview = data?.platformOverview;
  const everyone = data?.population === 'all';
  // Surface the k-anon floor from the payload when present; else default 5.
  const minCohort = report?.kAnonymityMin ?? report?.min ?? data?.institution?.kAnonymityMin ?? 5;
  const belowFloor = !loading && Boolean(report?.suppressed) && !platformOverview;
  // The program clears the floor; only this segment split is hidden, so the
  // consent and seats copy would be false here.
  const segmentSplit = data?.suppressionReason === 'segment_split';
  // Pills follow the last answer, not the fetch, so a refetch does not flash
  // them; on the very first load they wait for an answer.
  const hidePills = (loading && !data) || (Boolean(report?.suppressed) && !platformOverview);
  const referralCaption = 'Referral conversion: share of students with at least one completed coffee chat who got a referral.';
  return (
    <Card>
      {/* The portal page h1 already reads "Analytics"; the console drill-in keeps it. */}
      <SectionTitle hideTitle={portal}>Analytics</SectionTitle>
      {!hidePills || segment !== 'all' ? (
        <div className="flex flex-wrap mb-4">
          {['all', 'student', 'alumni'].map((seg) => {
            return <Pill key={seg} label={seg} active={segment === seg} onClick={() => { return setSegment(seg); }} />;
          })}
        </div>
      ) : null}
      {!hidePills || institutionId ? (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          {!hidePills ? (
            <div className="flex flex-wrap" aria-label="Analytics range">
              {[1, 4, 12, 26, 52].map((value) => {
                return <Pill key={value} label={`${value}w`} active={weeks === value} onClick={() => { setWeeks(value); }} />;
              })}
            </div>
          ) : null}
          {institutionId ? (
            <label className="flex items-center gap-2 text-sm" style={{ fontFamily: t.fonts.body, color: t.body }}>
              <span>Include non-program users</span>
              <Switch checked={includeNonProgram} onChange={setIncludeNonProgram} />
            </label>
          ) : null}
        </div>
      ) : null}

      {loading && <AnalyticsLoadingSkeleton everyone={includeNonProgram} timeSaved={readTimeSavedHint(hintKey)} />}
      {!loading && failed && (
        <ErrorState message="Couldn’t load analytics." onRetry={() => { load(segment); }} />
      )}
      {!loading && !failed && !report && (
        <p className="text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>Analytics unavailable.</p>
      )}
      {!loading && platformOverview && (
        <div className="mb-5">
          <StatRow>
            <Stat
              label="Student adoption"
              value={`${num(platformOverview.consentedCount)} / ${num(platformOverview.eligibleStudents)}`}
            />
            <Stat label="Seats used" value={`${platformOverview.seats?.used ?? 0} / ${platformOverview.seats?.purchased ?? 0}`} />
            {everyone ? <Stat label="Users included" value={num(platformOverview.populationCount)} /> : null}
            <Stat label="Coffee chats" value={num(platformOverview.coffeeChats)} />
            <Stat label="Held a chat" value={pct(platformOverview.participationRate)} />
            <Stat label="Referral conversion" value={pct(platformOverview.referralConversionRate)} />
            <Stat label="Avg rating" value={platformOverview.avgRating ? platformOverview.avgRating.toFixed(1) : 'N/A'} />
            <Stat label={everyone ? 'Chat min per user' : 'Chat min per student'} value={num(platformOverview.avgCoffeeChatMinutes)} />
          </StatRow>
          <Note className="mt-2">{referralCaption}</Note>
          <Note className="mt-2">
            {everyone
              ? 'Includes everyone with a verified school email, sponsored or not. Platform administrators only; schools never see this view.'
              : 'Exact tenant-wide aggregates for platform administrators. No individual student records are included.'}
          </Note>
        </div>
      )}
      {!loading && report?.suppressed && platformOverview && (
        <div>
          <StatusBadge tone="neutral" label="Breakdowns anonymized" />
          <p className="mt-2 text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>
            {`Aggregate totals are available above. Demographic breakdowns need ${minCohort}+ students to remain anonymous.`}
          </p>
        </div>
      )}
      {belowFloor && segmentSplit && (
        <div>
          <StatusBadge tone="neutral" label="Anonymized" />
          <p className="mt-3 text-base" style={{ fontFamily: t.fonts.body, color: t.body }}>
            This view is hidden to keep a small group anonymous. Use All to see your program.
          </p>
        </div>
      )}
      {belowFloor && !segmentSplit && (
        <div>
          <StatusBadge tone="sand" label="Below cohort min" />
          <p className="mt-3 text-base" style={{ fontFamily: t.fonts.body, color: t.body }}>
            {`Reports unlock automatically once ${minCohort}+ students have consented.`}
          </p>
          {canManageSeats ? (
            <a
              href={VIEW_TO_PATH.seats}
              className={`mt-2 inline-flex items-center min-h-[44px] text-base underline underline-offset-4 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
              style={{ color: t.heading, fontFamily: t.fonts.body }}
            >
              Go to Seats &amp; funding
            </a>
          ) : null}
        </div>
      )}
      {!loading && report && !report.suppressed && !platformOverview && (
        <div>
          <StatRow>
            <Stat label="Students opted in" value={num(report.adoption?.consentedCount)} />
            <Stat label="Seats used" value={`${report.adoption?.seats?.used ?? 'N/A'} / ${report.adoption?.seats?.purchased ?? 'N/A'}`} />
            <Stat label="Held a chat" value={pct(report.outcomes?.participationRate)} />
            <Stat label="Referral conversion" value={pct(report.outcomes?.referralConversionRate)} />
            <Stat label="Avg rating" value={report.outcomes?.avgRating ? report.outcomes.avgRating.toFixed(1) : 'N/A'} />
            <Stat label="Chat min per student" value={num(report.engagement?.avgCoffeeChatMinutes)} />
            <Stat label="Avg emails" value={num(report.activity?.avgEmailsSent)} />
          </StatRow>
          <Note className="mt-2">{referralCaption}</Note>
          <CohortTable title="Referral rate by major" rows={report.outcomes?.byMajor} labelKey="major" />
          <CohortTable title="Referral rate by graduation year" rows={report.outcomes?.byGraduationYear} labelKey="graduationYear" />
          <CohortTable
            title="Referral rate by industry"
            rows={report.outcomes?.byIndustry}
            labelKey="industry"
            countLabel="Chats"
            countKey="chats"
          />
          <ReferralCoverageNote coverage={report.outcomes?.referralCoverage} />
        </div>
      )}
      {/* Outside the school-report block, which needs !platformOverview, so the
          console drill-in (always sent platformOverview) shows it too. */}
      {!loading && !belowFloor && data?.timeSaved && !data.timeSaved.suppressed
        ? <TimeSavedSection timeSaved={data.timeSaved} />
        : null}
      {!loading && !belowFloor ? <ProgramTimeSeries trends={data?.trends} signals={data?.signals} everyone={everyone} /> : null}
      {!loading && !belowFloor ? <OutreachAudience audience={data?.audience} /> : null}
      {!loading ? <UsageAnalytics usage={data?.usage} /> : null}
      {!loading && !belowFloor ? <CoffeeChatSignals signals={data?.signals} /> : null}
    </Card>
  );
}
